import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2, Camera, ImagePlus, X } from "lucide-react";
import api from "@/lib/api";
import { HEARD_FROM, INTERESTS, GENDERS } from "@/lib/constants";
import Wordmark from "@/components/Wordmark";
import VisitorPass from "@/components/VisitorPass";
import { toast } from "sonner";

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-4 py-3 rounded-2xl text-sm font-semibold border-2 transition-all min-h-[48px] text-left ${
      active ? "bg-[#081226] text-white border-[#E8752A]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
    }`}>
    {children}
  </button>
);

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-2">
      {label}{required && <span className="text-[#D95D1E]"> *</span>}
    </label>
    {children}
  </div>
);

const derivePurpose = (interests = []) => {
  const has = (x) => interests.includes(x);
  if (has("Swimming")) return "Swimming";
  if (has("Gym") || has("Fitness Studio") || has("Wellness")) return "Gym / Fitness";
  if (has("Badminton") || has("Tennis") || has("Pickleball") || has("Cricket")) return "Sports";
  if (has("Membership") || has("Family Membership")) return "Membership";
  if (has("Café")) return "Café";
  if (has("Kids Activities") || has("Leisure")) return "Family Visit";
  return "Enquiry";
};

export default function Register() {
  const [params] = useSearchParams();
  const qrCode = params.get("qr") || "Main Gate";
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [pass, setPass] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    full_name: "", mobile: "", age: "", gender: "", area: "", pincode: "",
    visiting_for: "", first_time: "", heard_from: "", interests: [],
    wants_contact: "Yes", preferred_contact: "WhatsApp", consent: true,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInterest = (i) =>
    setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));

  const [docs, setDocs] = useState({ front: null, back: null });
  const [docBusy, setDocBusy] = useState("");

  const uploadDoc = (side, file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Image too large (max 6MB)"); return; }
    setDocBusy(side);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await api.post("/ekyc/upload", { mobile: form.mobile.trim(), side, image: reader.result });
        setDocs((d) => ({ ...d, [side]: { id: res.data.doc_id, preview: reader.result } }));
        toast.success(`Aadhaar ${side} uploaded`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Upload failed, please retry", { duration: 6000, style: { background: "#ffffff", color: "#b91c1c", border: "1px solid #fecaca", fontWeight: 600 } });
      } finally { setDocBusy(""); }
    };
    reader.readAsDataURL(file);
  };

  const removeDoc = async (side) => {
    const id = docs[side]?.id;
    setDocs((d) => ({ ...d, [side]: null }));
    if (id) { try { await api.delete(`/ekyc/doc/${id}`); } catch (e) {} }
  };

  const step1Valid = form.full_name.trim() && /^\d{10}$/.test(form.mobile.trim()) && !!docs.front?.id && !!docs.back?.id;
  const step2Valid = (form.wants_contact !== "Yes" || form.consent);

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      const res = await api.post("/register", {
        ...form, visiting_for: derivePurpose(form.interests), ekyc_verified: false,
        aadhaar_front_id: docs.front?.id || null, aadhaar_back_id: docs.back?.id || null,
        age: form.age ? parseInt(form.age) : null, qr_code: qrCode,
      });
      setPass(res.data);
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = useMemo(() => (pass ? 100 : (step / 3) * 100), [step, pass]);

  if (pass) return <VisitorPass pass={pass} />;

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-6"
      style={{ background: "linear-gradient(180deg,#081226,#0D1C38 55%,#081226)" }}>
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-5"><Wordmark /></div>

        <div className="bg-white rounded-3xl shadow-2xl border border-[#E8752A]/30 overflow-hidden">
          <div className="px-6 pt-6 pb-4 border-b border-slate-100">
            <h1 className="font-display text-2xl font-extrabold text-[#081226]">Welcome to Oneiros Varachha</h1>
            <p className="text-sm text-slate-500 mt-1">Tell us a little about your visit.</p>
            <div className="mt-4">
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: "linear-gradient(135deg,#FFA14D,#E85D1F)" }} />
              </div>
              <div className="flex justify-between mt-2 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                <span className={step >= 1 ? "text-[#B8860B]" : ""}>1 · Your Details</span>
                <span className={step >= 2 ? "text-[#B8860B]" : ""}>2 · Your Interest</span>
                <span className={step >= 3 ? "text-[#B8860B]" : ""}>3 · Confirm</span>
              </div>
            </div>
          </div>

          <div className="p-6">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                  <Field label="Full Name" required>
                    <input data-testid="visitor-name-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                      placeholder="Enter your name" className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#E8752A] outline-none text-base" />
                  </Field>
                  <Field label="Mobile Number" required>
                    <input data-testid="visitor-mobile-input" value={form.mobile} inputMode="numeric" maxLength={10}
                      onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))}
                      placeholder="10-digit mobile" className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#E8752A] outline-none text-base" />
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Age">
                      <input data-testid="visitor-age-input" value={form.age} inputMode="numeric" onChange={(e) => set("age", e.target.value.replace(/\D/g, ""))}
                        placeholder="Age" className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#E8752A] outline-none text-base" />
                    </Field>
                    <Field label="Gender">
                      <div className="grid grid-cols-3 gap-1.5">
                        {GENDERS.map((g) => (
                          <button key={g} type="button" onClick={() => set("gender", g)} data-testid={`gender-${g.toLowerCase()}-btn`}
                            className={`h-12 rounded-xl text-xs font-semibold border-2 ${form.gender === g ? "bg-[#081226] text-white border-[#E8752A]" : "bg-white border-slate-200 text-slate-600"}`}>
                            {g}
                          </button>
                        ))}
                      </div>
                    </Field>
                  </div>
                  <Field label="Area / Address">
                    <input data-testid="visitor-area-input" value={form.area} onChange={(e) => set("area", e.target.value)}
                      placeholder="e.g. Mota Varachha" className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#E8752A] outline-none text-base" />
                  </Field>
                  <Field label="Pin Code">
                    <input data-testid="visitor-pincode-input" value={form.pincode} inputMode="numeric" maxLength={6}
                      onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))}
                      placeholder="e.g. 394101" className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#E8752A] outline-none text-base" />
                  </Field>
                  <Field label="Upload Aadhaar Card" required>
                    <p className="text-xs text-slate-400 -mt-1 mb-2">Required — capture a photo or choose from gallery, for the front &amp; back.</p>
                    <div className="grid grid-cols-2 gap-3">
                      {["front", "back"].map((side) => (
                        <div key={side} className="relative">
                          <input id={`aadhaar-${side}-capture`} type="file" accept="image/*" capture="environment" className="hidden"
                            data-testid={`aadhaar-${side}-capture-input`}
                            onChange={(e) => { const f = e.target.files?.[0]; uploadDoc(side, f); e.target.value = ""; }} />
                          <input id={`aadhaar-${side}-gallery`} type="file" accept="image/*" className="hidden"
                            data-testid={`aadhaar-${side}-gallery-input`}
                            onChange={(e) => { const f = e.target.files?.[0]; uploadDoc(side, f); e.target.value = ""; }} />
                          {docs[side]?.preview ? (
                            <div className="relative aspect-[1.58/1] rounded-xl overflow-hidden border-2 border-emerald-300">
                              <img src={docs[side].preview} alt={`Aadhaar ${side}`} className="w-full h-full object-cover" />
                              <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded capitalize">{side} ✓</span>
                            </div>
                          ) : (
                            <div className="aspect-[1.58/1] rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center gap-2 p-2">
                              <span className="text-[11px] font-bold text-slate-600 capitalize">Aadhaar {side}</span>
                              {docBusy === side ? (
                                <Loader2 size={18} className="animate-spin text-[#E8752A]" />
                              ) : (
                                <div className="flex gap-1.5 w-full">
                                  <label htmlFor={`aadhaar-${side}-capture`} data-testid={`aadhaar-${side}-capture-btn`}
                                    className="flex-1 flex items-center justify-center gap-1 h-9 rounded-lg bg-[#081226] text-white text-[11px] font-semibold cursor-pointer">
                                    <Camera size={13} /> Capture
                                  </label>
                                  <label htmlFor={`aadhaar-${side}-gallery`} data-testid={`aadhaar-${side}-gallery-btn`}
                                    className="flex-1 flex items-center justify-center gap-1 h-9 rounded-lg border-2 border-[#E8752A]/60 text-[#D95D1E] text-[11px] font-semibold cursor-pointer">
                                    <ImagePlus size={13} /> Gallery
                                  </label>
                                </div>
                              )}
                            </div>
                          )}
                          {docs[side]?.preview && (
                            <button type="button" data-testid={`aadhaar-${side}-remove`} onClick={() => removeDoc(side)}
                              title="Remove / retake"
                              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center shadow hover:bg-rose-600 z-10">
                              <X size={13} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {!(docs.front?.id && docs.back?.id) && (
                      <p className="text-xs text-[#D95D1E] font-semibold mt-2" data-testid="aadhaar-required-hint">Both front &amp; back photos are required to continue.</p>
                    )}
                  </Field>
                  <button data-testid="step1-continue-btn" disabled={!step1Valid} onClick={() => setStep(2)}
                    className="w-full h-14 rounded-2xl font-bold text-lg text-white disabled:opacity-40 flex items-center justify-center gap-2 transition-transform active:scale-[0.99]"
                    style={{ background: "linear-gradient(135deg,#FFA14D,#E85D1F)" }}>
                    Continue <ArrowRight size={20} />
                  </button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                  <Field label="First time visiting?">
                    <div className="grid grid-cols-2 gap-2">
                      {["Yes", "No"].map((v) => (
                        <Chip key={v} active={form.first_time === v} onClick={() => set("first_time", v)} testid={`firsttime-${v.toLowerCase()}-chip`}>{v}</Chip>
                      ))}
                    </div>
                  </Field>
                  <Field label="How did you hear about us?">
                    <div className="grid grid-cols-2 gap-2">
                      {HEARD_FROM.map((v) => (
                        <Chip key={v} active={form.heard_from === v} onClick={() => set("heard_from", v)} testid={`heard-${v.replace(/[^a-z]/gi, "").toLowerCase()}-chip`}>{v}</Chip>
                      ))}
                    </div>
                  </Field>
                  <Field label="What are you interested in?">
                    <div className="flex flex-wrap gap-2">
                      {INTERESTS.map((v) => (
                        <button key={v} type="button" onClick={() => toggleInterest(v)} data-testid={`interest-${v.replace(/[^a-z]/gi, "").toLowerCase()}-chip`}
                          className={`px-3.5 py-2.5 rounded-full text-sm font-semibold border-2 transition-all ${form.interests.includes(v) ? "bg-[#D95D1E] text-white border-[#D95D1E]" : "bg-white text-slate-600 border-slate-200"}`}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <div className="flex gap-3 pt-1">
                    <button data-testid="step2-back-btn" onClick={() => setStep(1)} className="h-14 px-5 rounded-2xl font-semibold border-2 border-slate-200 text-slate-600 flex items-center gap-1"><ArrowLeft size={18} /></button>
                    <button data-testid="step2-continue-btn" onClick={() => setStep(3)}
                      className="flex-1 h-14 rounded-2xl font-bold text-lg text-white disabled:opacity-40 flex items-center justify-center gap-2"
                      style={{ background: "linear-gradient(135deg,#FFA14D,#E85D1F)" }}>
                      Continue <ArrowRight size={20} />
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                  <Field label="Would you like our team to contact you?">
                    <div className="grid grid-cols-2 gap-2">
                      {["Yes", "No"].map((v) => (
                        <Chip key={v} active={form.wants_contact === v} onClick={() => set("wants_contact", v)} testid={`contact-${v.toLowerCase()}-chip`}>{v}</Chip>
                      ))}
                    </div>
                  </Field>
                  {form.wants_contact === "Yes" && (
                    <>
                      <Field label="Preferred contact option">
                        <div className="grid grid-cols-2 gap-2">
                          {["WhatsApp", "Phone Call"].map((v) => (
                            <Chip key={v} active={form.preferred_contact === v} onClick={() => set("preferred_contact", v)} testid={`prefcontact-${v.replace(/\s/g, "").toLowerCase()}-chip`}>{v}</Chip>
                          ))}
                        </div>
                      </Field>
                      <label className="flex items-start gap-3 bg-slate-50 rounded-2xl p-4 cursor-pointer border border-slate-100">
                        <input type="checkbox" data-testid="consent-checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 w-5 h-5 accent-[#E8752A]" />
                        <span className="text-xs text-slate-600 leading-relaxed">
                          I agree to be contacted by the Oneiros Varachha team regarding my enquiry and membership information.
                        </span>
                      </label>
                    </>
                  )}
                  <div className="bg-[#081226] rounded-2xl p-4 text-white text-sm space-y-1">
                    <p className="font-display font-bold text-[#E8752A] mb-2">Review</p>
                    <p><span className="text-slate-400">Name:</span> {form.full_name}</p>
                    <p><span className="text-slate-400">Mobile:</span> {form.mobile}</p>
                    {(docs.front?.id || docs.back?.id) && <p><span className="text-slate-400">Aadhaar:</span> {[docs.front?.id && "Front", docs.back?.id && "Back"].filter(Boolean).join(" + ")} uploaded</p>}
                    {form.interests.length > 0 && <p><span className="text-slate-400">Interests:</span> {form.interests.join(", ")}</p>}
                  </div>
                  {error && <p className="text-sm text-rose-600 text-center">{error}</p>}
                  <div className="flex gap-3">
                    <button data-testid="step3-back-btn" onClick={() => setStep(2)} className="h-14 px-5 rounded-2xl font-semibold border-2 border-slate-200 text-slate-600 flex items-center gap-1"><ArrowLeft size={18} /></button>
                    <button data-testid="visitor-submit-btn" disabled={!step2Valid || submitting} onClick={submit}
                      className="flex-1 h-14 rounded-2xl font-bold text-lg text-white disabled:opacity-50 flex items-center justify-center gap-2"
                      style={{ background: "linear-gradient(90deg,#D95D1E,#C04A0E)" }}>
                      {submitting ? <Loader2 className="animate-spin" size={20} /> : <><Check size={20} /> Confirm & Get Pass</>}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <p className="text-center text-xs text-slate-500 mt-4">Entry point: {qrCode}</p>
      </div>
    </div>
  );
}
